import type { UsageFeatures, UsageOrganisationType } from 'c-sdk';
import { allRouteTemplates, areaFor, type UsageArea } from './usageAreas';

/**
 * The figures behind the feature usage page, from the API's per-page numbers and the app's own
 * route list (the API only knows pages somebody visited).
 */

export const isStaffOnly = (template: string): boolean =>
    areaFor(template) === 'admin' || template.startsWith('/admin');

export const ROLES: UsageOrganisationType[] = ['agency', 'owner', 'buyer', 'client'];
const REGULAR_PEOPLE = 10;
const RARE_PEOPLE = 3;
/** Pages in the adoption grid beside the tracked actions: the ones with most time. */
const ADOPTION_PAGES = 5;

type FeaturePage = UsageFeatures['pages'][number];
type RoleShares = FeaturePage['roles'];

export type NeverUsedGroup = { area: UsageArea; templates: string[] };
export type AdoptionRow = { key: string; kind: 'page' | 'event'; name: string; roles: RoleShares };

export type FeatureSummary = {
    /** Pages people can open: every route, less admin pages while staff are left out. */
    pageCount: number;
    /** Visited pages that are real routes (sign-in pages are logged too), most time first. */
    used: FeaturePage[];
    /** Used pages with time on them: what "most used" ranks. */
    ranked: FeaturePage[];
    regular: number;
    /** Share (0-1) of all page time the regular pages carry. */
    regularShare: number;
    rare: number;
    neverUsed: NeverUsedGroup[];
    neverUsedCount: number;
    adoption: AdoptionRow[];
};

export const summariseFeatures = (
    features: UsageFeatures,
    includeStaff: boolean,
): FeatureSummary => {
    // Only staff can open admin pages (the admin area, and anything under /admin such as the
    // screen admin), so with staff left out they would always read "never used".
    const pool = allRouteTemplates().filter(template => includeStaff || !isStaffOnly(template));
    const routes = new Set(allRouteTemplates());
    const used = features.pages.filter(page => routes.has(page.name));
    const ranked = used.filter(page => page.time_ms > 0);
    const regularPages = used.filter(page => page.people >= REGULAR_PEOPLE);

    const visited = new Set(used.map(page => page.name));
    const groups = new Map<UsageArea, string[]>();
    pool.filter(template => !visited.has(template)).forEach(template => {
        const area = areaFor(template);
        groups.set(area, [...(groups.get(area) ?? []), template]);
    });
    const neverUsed = [...groups.entries()]
        .map(([area, templates]) => ({ area, templates }))
        .sort((a, b) => b.templates.length - a.templates.length);

    const adoption: AdoptionRow[] = [
        ...ranked.slice(0, ADOPTION_PAGES).map(page => ({
            key: `page:${page.name}`,
            kind: 'page' as const,
            name: page.name,
            roles: page.roles,
        })),
        ...features.events.map(event => ({
            key: `event:${event.name}`,
            kind: 'event' as const,
            name: event.name,
            roles: event.roles,
        })),
    ];

    return {
        pageCount: pool.length,
        used,
        ranked,
        regular: regularPages.length,
        regularShare: regularPages.reduce((sum, page) => sum + page.time_share, 0),
        rare: used.filter(page => page.people > 0 && page.people <= RARE_PEOPLE).length,
        neverUsed,
        neverUsedCount: neverUsed.reduce((sum, group) => sum + group.templates.length, 0),
        adoption,
    };
};

/**
 * A role's share for one feature: null when nobody in that role was active at all, 0 when they
 * were but nobody used it.
 */
export const roleShare = (
    roles: RoleShares,
    role: UsageOrganisationType,
    activeByRole: UsageFeatures['active_users_by_role'],
): number | null => {
    if (!activeByRole[role]) return null;
    return roles[role]?.share ?? 0;
};
